import { useState } from "react";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, fmt, money } from "@/lib/api";
import { SignalBadge, Pnl, PageHeader } from "@/components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMOTIONS = ["", "Calm", "Confident", "Anxious", "FOMO", "Greedy", "Fearful", "Impatient"];

function EditDialog({ trade, onClose, onSaved }) {
  const [f, setF] = useState({ notes: trade.notes || "", emotion: trade.emotion || "", lesson: trade.lesson || "", tags: (trade.tags || []).join(", ") });
  const save = async () => {
    try {
      await api.patch(`/journal/${trade.id}`, { ...f, tags: f.tags.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Journal entry saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "w-full bg-white border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#007AFF]";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-zinc-200 rounded-sm" data-testid="journal-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Journal · {trade.symbol} {trade.direction}</DialogTitle></DialogHeader>
        <label className="space-y-1"><span className="label-xs">Notes</span><textarea rows={3} className={inp} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="journal-notes-input" /></label>
        <label className="space-y-1"><span className="label-xs">Emotion</span>
          <select className={inp} value={f.emotion} onChange={(e) => setF({ ...f, emotion: e.target.value })} data-testid="journal-emotion-select">{EMOTIONS.map((x) => <option key={x}>{x}</option>)}</select></label>
        <label className="space-y-1"><span className="label-xs">Lesson learned</span><input className={inp} value={f.lesson} onChange={(e) => setF({ ...f, lesson: e.target.value })} data-testid="journal-lesson-input" /></label>
        <label className="space-y-1"><span className="label-xs">Tags (comma separated)</span><input className={inp} value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="journal-tags-input" /></label>
        <button onClick={save} className="py-2 bg-[#007AFF] hover:bg-[#1a86ff] rounded-sm text-sm font-semibold transition-colors text-white" data-testid="journal-save-btn">Save</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Journal() {
  const trades = usePoll("/paper/trades?status=closed", 0);
  const stats = usePoll("/journal/stats", 0);
  const acct = usePoll("/paper/account", 0);
  const [edit, setEdit] = useState(null);
  const s = stats.data;
  const ccy = acct.data?.currency || "USD";
  const dg = (x) => (x.endsWith("JPY") ? 3 : 5);
  return (
    <div data-testid="journal-page">
      <PageHeader title="Trade Journal" sub="Automatically logged from paper trades · annotate to learn" />
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        {[["Trades", s?.total, "journal-total"], ["Win Rate", s?.win_rate != null ? `${s.win_rate}%` : "—", "journal-win-rate"],
          ["Profit Factor", s?.profit_factor ?? "—", "journal-pf"], ["Net P/L", <Pnl v={s?.net_pnl}>{money(s?.net_pnl, ccy)}</Pnl>, "journal-net"],
          ["Avg Win", money(s?.avg_win, ccy), "journal-avg-win"], ["Avg Loss", money(s?.avg_loss, ccy), "journal-avg-loss"]].map(([l, v, t]) => (
          <div key={l} className="panel p-4" data-testid={t}><p className="label-xs mb-2">{l}</p><div className="num text-lg text-zinc-900">{v ?? "—"}</div></div>
        ))}
      </div>
      {s?.by_pair?.length > 0 && (
        <div className="panel p-4 mb-4" data-testid="journal-by-pair">
          <p className="label-xs mb-3">Performance by pair</p>
          <div className="flex flex-wrap gap-2">{s.by_pair.map((p) => (
            <div key={p.symbol} className="border border-zinc-200 px-3 py-2 text-xs"><span className="num text-zinc-900 mr-2">{p.symbol}</span><Pnl v={p.pnl}>{money(p.pnl, ccy)}</Pnl><span className="text-zinc-500 ml-2">{p.wins}/{p.trades}W</span></div>
          ))}</div>
        </div>
      )}
      <div className="panel overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm" data-testid="journal-table">
          <thead className="bg-zinc-50 border-b border-zinc-200"><tr>{["Date", "Pair", "Side", "Entry", "Exit", "Result", "P/L", "Conf.", "Session", "Emotion", "Notes", ""].map((h) => <th key={h} className="px-3 py-2 text-left label-xs">{h}</th>)}</tr></thead>
          <tbody>
            {(trades.data || []).map((t) => (
              <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`journal-row-${t.id}`}>
                <td className="px-3 py-2.5 num text-[11px] text-zinc-500 whitespace-nowrap">{new Date(t.opened_at).toUTCString().slice(5, 22)}</td>
                <td className="px-3 py-2.5 num font-bold">{t.symbol}</td><td className="px-3 py-2.5"><SignalBadge signal={t.direction} /></td>
                <td className="px-3 py-2.5 num text-xs">{fmt(t.entry, dg(t.symbol))}</td><td className="px-3 py-2.5 num text-xs">{fmt(t.close_price, dg(t.symbol))}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">{t.close_reason}</td>
                <td className="px-3 py-2.5 text-xs"><Pnl v={t.pnl}>{money(t.pnl, ccy)}</Pnl></td>
                <td className="px-3 py-2.5 num text-xs text-[#7C3AED]">{t.confidence != null ? `${t.confidence}%` : "Manual"}</td>
                <td className="px-3 py-2.5 text-[11px] text-zinc-500">{(t.session || []).join(", ")}</td>
                <td className="px-3 py-2.5 text-xs">{t.emotion || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500 max-w-[220px] truncate">{t.notes || t.lesson || "—"}</td>
                <td className="px-3 py-2.5"><button onClick={() => setEdit(t)} className="text-[11px] text-[#007AFF] hover:underline" data-testid={`journal-edit-${t.id}`}>Annotate</button></td>
              </tr>
            ))}
            {!trades.data?.length && <tr><td colSpan={12} className="p-8 text-center text-sm text-zinc-500" data-testid="journal-empty">Closed paper trades appear here automatically.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <EditDialog trade={edit} onClose={() => setEdit(null)} onSaved={trades.reload} />}
    </div>
  );
}
